import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createMilestone, updateMilestone } from "app/services/project.service";

const MILESTONE_STATUSES = ["PENDING", "IN_PROGRESS", "COMPLETED", "DELAYED", "ON_HOLD"];

const initialData = {
  id: null,
  milestoneName: "",
  description: "",
  projectId: "",
  projectName: "",
  dueDate: "",
  owner: "",
  status: "PENDING",
  completionPercentage: 0,
};

const AddMilestoneDialog = ({ open, setOpen, onSuccess, editObject, projects = [] }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({
        ...editObject,
        dueDate: editObject.dueDate?.substring(0, 10) || "",
      });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.milestoneName || data.milestoneName.trim() === "") {
      setError("Milestone name is required");
      return false;
    }
    if (!data.projectId) {
      setError("Project is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      if (editObject) {
        await updateMilestone(data.id, data);
        setSuccess("Milestone updated successfully");
      } else {
        await createMilestone(data);
        setSuccess("Milestone created successfully");
      }
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Milestone" : "Add Milestone"}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <TextField
            id="projectId"
            label="Project"
            fullWidth
            select
            variant="outlined"
            value={data.projectId}
            onChange={(e) => {
              const project = projects.find((p) => p.id === e.target.value);
              setData({ ...data, projectId: e.target.value, projectName: project?.projectName || "" });
            }}
            required
            disabled={!!editObject}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="milestoneName"
            label="Milestone Name"
            fullWidth
            variant="outlined"
            value={data.milestoneName}
            onChange={(e) => setData({ ...data, milestoneName: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="dueDate"
            label="Due Date"
            type="date"
            fullWidth
            variant="outlined"
            value={data.dueDate}
            onChange={(e) => setData({ ...data, dueDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            id="owner"
            label="Owner"
            fullWidth
            variant="outlined"
            value={data.owner}
            onChange={(e) => setData({ ...data, owner: e.target.value })}
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="status"
            label="Status"
            fullWidth
            select
            variant="outlined"
            value={data.status}
            onChange={(e) => setData({ ...data, status: e.target.value })}
          >
            {MILESTONE_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s.replace("_", " ")}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="completionPercentage"
            label="Completion %"
            type="number"
            fullWidth
            variant="outlined"
            value={data.completionPercentage}
            onChange={(e) => setData({ ...data, completionPercentage: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
            inputProps={{ min: 0, max: 100 }}
          />
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddMilestoneDialog);

